// mtStatusColor — shared helper mixin for Modern Trade (MT) badge colors.
//
// Round-3 (#164/#165): the old badge-* Bootstrap classes render dark, saturated
// pills that are hard to read. This mixin returns *light-tone* inline styles
// (soft background + dark text) plus the matching i18n label, so every MT view
// renders sale_type / status consistently.
//
// Usage:
//   import mtStatusColor from "@/mixins/mtStatusColor";
//   export default { mixins: [mtStatusColor], ... }
//   <span class="mt-pill" :style="mtStatusStyle(row.status)">{{ mtStatusLabel(row.status) }}</span>
//   <span class="mt-pill" :style="mtSaleTypeStyle(row.sale_type)">{{ mtSaleTypeLabel(row.sale_type) }}</span>
//
// The `.mt-pill` class (padding / radius / font) is provided by each component or
// can be inlined; this mixin only owns the color tokens + labels.

// status code → { bg, text } (light background, dark readable text)
const STATUS_COLORS = {
  1: { bg: "#f1f3f5", text: "#495057" }, // draft (ร่าง) — neutral grey
  2: { bg: "#e7f1ff", text: "#1c5dbd" }, // confirmed (ยืนยันแล้ว) — blue
  // amber text measured 3.89:1 on its own bg — the only pair in this file below
  // AA. #995b00 keeps hue 35.7 / sat 100% and measures 5.00:1.
  3: { bg: "#fff4e0", text: "#995b00" }, // in_production (กำลังผลิต) — amber
  4: { bg: "#fdf0d5", text: "#8a6100" }, // goods_received (รับสินค้าแล้ว) — gold
  5: { bg: "#e8eaff", text: "#3d3fb3" }, // delivered (จัดส่งแล้ว) — indigo
  6: { bg: "#e3f6e8", text: "#1e7d43" }, // closed (ปิดงาน) — green
  9: { bg: "#fdecec", text: "#c0392b" }, // cancelled (ยกเลิก) — red
};

// sale_type code → { bg, text }
const SALE_TYPE_COLORS = {
  1: { bg: "#e2f6f4", text: "#0f766e" }, // consignment (ฝากขาย) — teal
  2: { bg: "#f0e9fb", text: "#6b3fa0" }, // outright (ขายขาด) — purple
};

// round-19 Dev-B — delivery ship_status code → { bg, text }
const SHIP_STATUS_COLORS = {
  1: { bg: "#fff4e0", text: "#995b00" }, // pending (รอส่งของ) — amber (5.00:1)
  2: { bg: "#e3f6e8", text: "#1e7d43" }, // shipped (ส่งของแล้ว) — green
};

const NEUTRAL = { bg: "#f1f3f5", text: "#495057" };

function toStyle(tok) {
  return {
    backgroundColor: tok.bg,
    color: tok.text,
    border: `1px solid ${tok.bg}`,
  };
}

export default {
  methods: {
    // ── styles ────────────────────────────────────────────────────────────────
    mtStatusStyle(status) {
      return toStyle(STATUS_COLORS[status] || NEUTRAL);
    },
    mtSaleTypeStyle(saleType) {
      return toStyle(SALE_TYPE_COLORS[saleType] || NEUTRAL);
    },
    // ── labels (reuse existing modern_trade.sale_order.* keys) ─────────────────
    mtStatusLabel(status) {
      switch (status) {
        case 1: return this.$t("modern_trade.sale_order.status_draft");
        case 2: return this.$t("modern_trade.sale_order.status_confirmed");
        case 3: return this.$t("modern_trade.sale_order.status_in_production");
        case 4: return this.$t("modern_trade.sale_order.status_goods_received");
        case 5: return this.$t("modern_trade.sale_order.status_delivered");
        case 6: return this.$t("modern_trade.sale_order.status_closed");
        case 9: return this.$t("modern_trade.sale_order.status_cancelled");
        default: return "-";
      }
    },
    mtSaleTypeLabel(saleType) {
      if (saleType === 1) return this.$t("modern_trade.sale_order.sale_type_consignment");
      if (saleType === 2) return this.$t("modern_trade.sale_order.sale_type_outright");
      return "-";
    },
    // round-19 Dev-B — delivery ship_status (1 รอส่งของ / 2 ส่งของแล้ว)
    mtShipStatusStyle(shipStatus) {
      return toStyle(SHIP_STATUS_COLORS[shipStatus] || NEUTRAL);
    },
    mtShipStatusLabel(shipStatus) {
      if (shipStatus === 2) return this.$t("modern_trade.delivery.ship_status.shipped");
      return this.$t("modern_trade.delivery.ship_status.pending");
    },
  },
};
